import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, ShieldCheck, Lightning, Globe, Users, UserCircle, LinkedinLogo, TwitterLogo } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import { BreadcrumbSchema } from '../components/StructuredData';
import ShareButtons from '../components/ShareButtons';

const teamMembers = [
  {
    name: 'Priya Ramesh',
    role: 'Senior Tax Advisor, UAE & International Tax',
    location: 'Dubai, UAE',
    tags: 'UAE Corporate Tax | VAT | FTA Compliance',
    bio: 'Priya brings 5 years of experience in UAE indirect and direct tax, with deep expertise in VAT return filing, Corporate Tax compliance, and FTA representations. She has worked with mainland and free zone entities across retail, logistics, and professional services.',
    image: 'https://customer-assets.emergentagent.com/job_jolly-payne-3/artifacts/xu5txe2m_Priya%20Ramesh.jpg',
  },
  {
    name: 'Rohan Mehta',
    role: 'Transfer Pricing & Cross-Border Advisory',
    location: 'Bangalore, India',
    tags: 'Transfer Pricing | DTAA | International Tax',
    bio: 'Rohan specialises in transfer pricing documentation, benchmarking studies, and related party transaction structuring for businesses operating across multiple jurisdictions. With hands-on experience in Form 15CA/15CB, DTAA benefit analysis, and Master File/Local File preparation.',
    image: 'https://customer-assets.emergentagent.com/job_jolly-payne-3/artifacts/4k1bkah8_Rohan%20Mehta.jpg',
  },
  {
    name: 'Swapnil Gupta',
    role: 'India Tax & Compliance Lead',
    location: 'Bangalore, India',
    tags: 'Direct Tax | ITR Filing | Tax Audit',
    bio: 'Swapnil manages end-to-end India income tax compliance for individuals, firms, and corporates — covering ITR filings, tax audit reports, and advance tax planning with depth in HNI portfolios, capital gains complexity, and presumptive taxation.',
    image: 'https://customer-assets.emergentagent.com/job_jolly-payne-3/artifacts/wzmz6pzp_Swapnil%20Guptaa.jpg',
  },
  {
    name: 'Rahul Nair',
    role: 'FP&A & Virtual CFO Specialist',
    location: 'Bangalore, India',
    tags: 'Financial Modelling | Board Reporting | Budgeting',
    bio: 'Rahul partners with founders and leadership teams as their embedded finance function — owning budgeting, forecasting, KPI dashboards, and investor-ready board packs. With prior experience in FP&A roles at growth-stage companies.',
    image: 'https://customer-assets.emergentagent.com/job_jolly-payne-3/artifacts/o010m1gy_Rahul%20Nair.jpg',
  },
  {
    name: 'Sravanthi',
    role: 'Audit & Assurance Manager',
    location: 'Bangalore, India',
    tags: 'Statutory Audit | Internal Audit | IFRS Reporting',
    bio: 'Sravanthi leads the firm\'s audit and assurance practice, handling internal audits, internal control reviews, and financial statement preparation under IFRS and Indian GAAP. She works closely with the CFO advisory team to ensure audit-ready books year-round.',
    image: 'https://customer-assets.emergentagent.com/job_jolly-payne-3/artifacts/cpm22tyw_Sravanthi.jpg',
  },
  {
    name: 'Mohammed Al Farsi',
    role: 'UAE Business Advisory & Banking Relations',
    location: 'Dubai, UAE',
    tags: 'Business Setup | Banking | Regulatory Compliance',
    bio: 'Mohammed brings local UAE market expertise across business setup, corporate banking relationships, and regulatory compliance for mainland and free zone entities. He helps clients navigate entity structuring, bank account opening, and UAE Commercial Companies Law.',
    image: 'https://customer-assets.emergentagent.com/job_jolly-payne-3/artifacts/fyz62p2m_Mohammed%20Al%20Farsi.jpg',
  },
];

export default function AboutPage() {
  return (
    <div>
      <SEO
        title="About - Finance Clarity for Ambitious Businesses"
        description="Meet the Ventriqo team led by founder Ankit Kumar, CA & FTA-approved Tax Agent with 14+ years experience. Expert pod model: Virtual CFO, Senior Reviewer, Bookkeeping Specialist."
        path="/about"
      />
      <BreadcrumbSchema items={[{ name: 'Home', path: '/' }, { name: 'About', path: '/about' }]} />

      {/* Hero */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-6">About Ventriqo</p>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-none text-black mb-6">
            Finance clarity<br />for ambitious<br /><span className="text-[#002FA7]">businesses</span>.
          </h1>
          <p className="text-base text-gray-600 max-w-2xl">
            We believe every founder deserves real-time financial visibility and strategic guidance.
            Ventriqo bridges the gap between daily operations and high-level financial decision-making.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-[#F9FAFB] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4">Our Mission</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black mb-6">
                Daily-updated books. Proactive insights. Not month-end chaos.
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Traditional bookkeeping is broken. Month-end surprises, delayed closes, and reactive compliance leave founders flying blind.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed">
                We combine AI-powered daily bookkeeping with fractional CFO services to deliver a comprehensive Finance-as-a-Service solution.
                From transactions to strategic growth, we've got you covered.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1759020687582-7d8e58527cba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBnbGFzcyUyMG9mZmljZSUyMGJ1aWxkaW5nJTIwaGlnaCUyMGNvbnRyYXN0fGVufDB8fHx8MTc3NTgwMzYwOXww&ixlib=rb-4.1.0&q=85"
                alt="Modern office"
                className="w-full border border-black/10"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4">Our Values</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black mb-12">What drives us</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-black/10">
            {[
              { icon: Lightning, title: 'Speed', desc: 'T+1 posting. Daily reconciliation. No more waiting until month-end.' },
              { icon: ShieldCheck, title: 'Accuracy', desc: '>98% classification accuracy backed by AI and human review.' },
              { icon: Globe, title: 'Global', desc: 'Multi-jurisdiction compliance across India, US, and UAE.' },
              { icon: Users, title: 'Proactive', desc: 'We surface insights before you ask. Strategic, not reactive.' },
            ].map((v, i) => (
              <div key={i} className={`p-8 ${i < 3 ? 'border-b md:border-b-0 md:border-r border-black/10' : ''}`}>
                <v.icon size={28} weight="duotone" className="text-[#002FA7] mb-4" />
                <h3 className="text-lg font-bold text-black mb-2">{v.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expert Pod Model */}
      <section className="bg-[#F9FAFB] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4">How We Work</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black mb-4">Expert Pod Model</h2>
          <p className="text-sm text-gray-600 max-w-3xl mb-12">
            Every client is served by a dedicated three-layer team — structured for accuracy at the transaction level, rigour at the review level, and strategic clarity at the advisory level.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black/10 bg-white">
            {[
              {
                title: 'Virtual CFO',
                desc: 'Your strategic finance partner — owns board reporting, financial forecasting, investor MIS, and cross-border advisory. One senior point of contact who understands your business, not just your numbers.',
              },
              {
                title: 'Senior Reviewer',
                desc: 'Every entry, every posting, every reconciliation is reviewed before it reaches you. Your quality gate — ensuring nothing is miscategorised, misclassified, or missed.',
              },
              {
                title: 'Bookkeeping Specialist',
                desc: 'Handles daily transaction ingestion, categorisation, bank reconciliation, and ledger maintenance — processing multiple transactions daily with structured checklists and zero backlog.',
              },
            ].map((t, i) => (
              <div key={i} className={`p-8 ${i < 2 ? 'border-b md:border-b-0 md:border-r border-black/10' : ''}`}>
                <div className="w-12 h-12 bg-[#002FA7]/10 rounded-full flex items-center justify-center mb-4">
                  <UserCircle size={28} weight="duotone" className="text-[#002FA7]" />
                </div>
                <h3 className="text-lg font-bold text-black mb-3">{t.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Founder */}
      <section data-testid="founder-section" className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4">Leadership</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black mb-12">Meet the Founder</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black/10">
            <div className="border-b md:border-b-0 md:border-r border-black/10 overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_jolly-payne-3/artifacts/v50d4eiv_Ankit%20Kumar.jpg"
                alt="Ankit Kumar - Founder, Ventriqo"
                className="w-full h-full object-cover object-top"
                data-testid="founder-image"
              />
            </div>
            <div className="md:col-span-2 p-8 md:p-12">
              <h3 className="text-2xl font-black tracking-tight text-black mb-1">Ankit Kumar</h3>
              <p className="text-sm font-semibold text-[#002FA7] mb-1">Director & Founder, Ventriqo Consulting</p>
              <p className="text-xs text-gray-500 mb-6">Dubai, UAE | Corporate Tax | Transfer Pricing | CFO Advisory</p>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Ankit is a Chartered Accountant and FTA-approved Tax Agent with over 14 years of experience across corporate finance, FP&A, audit, and tax advisory. He founded Ventriqo to bring institutional-grade financial leadership to founders, SMEs, and growing businesses — without the cost of a full-time hire.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-4">
                Having built his career across high-growth organisations including PharmEasy and Landmark Group where he led corporate finance and financial planning at a senior level — Ankit brings a rare combination of Big-4-trained technical rigour and real-world commercial judgment to every client engagement.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mb-6">
                Today, through Ventriqo, he works with businesses across India, USA and the UAE on Corporate Tax compliance, Transfer Pricing documentation, Virtual CFO mandates, and Financial Reporting under IFRS and US GAAP — handling complexity so his clients can focus on growth.
              </p>
              <blockquote className="border-l-2 border-[#002FA7] pl-4">
                <p className="text-sm text-gray-700 italic">
                  "I work with founders and finance teams who need a trusted advisor — someone who understands both the numbers and the business behind them."
                </p>
              </blockquote>
              <div className="mt-6 flex items-center gap-4">
                <a href="https://www.linkedin.com/in/ca-ankit-kumar-a805993a/" target="_blank" rel="noopener noreferrer" data-testid="founder-linkedin" className="w-9 h-9 flex items-center justify-center bg-[#0A66C2] text-white rounded-[4px] hover:opacity-90 transition-opacity" title="Ankit Kumar on LinkedIn">
                  <LinkedinLogo size={18} weight="bold" />
                </a>
                <a href="https://x.com/Ankit_classic" target="_blank" rel="noopener noreferrer" data-testid="founder-twitter" className="w-9 h-9 flex items-center justify-center bg-black text-white rounded-[4px] hover:opacity-90 transition-opacity" title="Ankit Kumar on X">
                  <TwitterLogo size={18} weight="bold" />
                </a>
                <span className="text-xs text-gray-400 ml-2">|</span>
                <ShareButtons url="https://www.ventriqo.com/about" title="Meet the team behind Ventriqo - Finance-as-a-Service" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section data-testid="team-section" className="bg-[#F9FAFB] border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4">The People</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black mb-12">Our Team</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-black/10 bg-white">
            {teamMembers.map((member, i) => (
              <div
                key={i}
                data-testid={`team-member-${i}`}
                className={`${i < teamMembers.length - 1 ? 'border-b md:border-b-0' : ''} ${(i % 3 !== 2) ? 'md:border-r border-black/10' : ''} ${i >= 3 ? 'md:border-t border-black/10' : ''}`}
              >
                {/* Team member image placeholder - replace with actual photo */}
                <div className="w-full aspect-[3/4] bg-gray-100 flex items-center justify-center overflow-hidden" data-testid={`team-image-placeholder-${i}`}>
                  {member.image ? (
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover object-top" />
                  ) : (
                    <UserCircle size={64} weight="duotone" className="text-gray-300" />
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-base font-bold text-black">{member.name}</h3>
                  <p className="text-xs text-[#002FA7] font-semibold mb-1">{member.role}</p>
                  <p className="text-xs text-gray-400 mb-1">{member.location}</p>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-3">{member.tags}</p>
                  <p className="text-xs text-gray-600 leading-relaxed">{member.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Target Clients */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#002FA7] mb-4">Who We Serve</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-black mb-8">Built for ambitious companies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-black/10">
            <div className="p-8 md:p-10 border-b md:border-b-0 md:border-r border-black/10">
              <h3 className="text-xl font-bold text-black mb-3">VC-Backed Indian SaaS / D2C</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Fast-growing companies that need daily financial visibility, GST compliance,
                and board-ready reporting without building an in-house finance team.
              </p>
            </div>
            <div className="p-8 md:p-10">
              <h3 className="text-xl font-bold text-black mb-3">Cross-Border Founders</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                US/UAE entities with India operations that need multi-jurisdiction compliance,
                FX reconciliation, and consolidated reporting across entities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">Let's talk numbers</h2>
          <p className="text-gray-400 mb-8 text-sm">Get a free finance health check and see what daily clarity looks like.</p>
          <Link to="/#lead-form">
            <Button data-testid="about-cta" className="bg-[#002FA7] hover:bg-[#002277] text-white rounded-[4px] px-8 py-3 text-sm font-semibold">
              Get Free Finance Health Check <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
